import { I18n } from '@calendly/i18n';
import { IconArrowNarrowRight } from '@calendly/ui/components/glyphs/IconArrowNarrowRight';
import { IconX } from '@calendly/ui/components/glyphs/IconX';
import { IconButton } from '@calendly/ui/components/icon-button';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorTextSecondary, colorTextStandard } from '@calendly/ui/theme';
import type { FC } from 'react';
import tourCardPreview from './images/brand-refresh-tour-card.png';

/** Outer wrapper that holds the card and its overlaid dismiss control within the nav footer. */
const containerCss = "c154fswr";

/** The card surface containing the preview illustration and the content area. */
const cardCss = "c19vjmzp";

/** The preview illustration shown at the top of the card. */
const previewCss = "pne74ng";

/** The content area below the preview: the title/body text group and the primary action. */
const contentCss = "c2xdmsv";

/** The card's title and body text. */
const textGroupCss = "tgkpvrm";

/** The dismiss (X) control, overlaid at the card's top-right corner. */
const dismissCss = "di0l9w2";

/** Props for {@link BrandRefreshTourCard}. */
export type BrandRefreshTourCardProps = {
  /** Invoked when the user clicks "Start the tour" — should launch the tour and dismiss the card. */
  onStartTour: () => void;
  /** Invoked when the user clicks the dismiss (X) control — should dismiss the card for good. */
  onDismiss: () => void;
};

/**
 * The "New Calendly tour card": the entry point for the Brand Refresh navigation tour, pinned to the
 * top of the vertical-nav footer via the CUI `VerticalNav` `customFooterItem` slot (expanded rail
 * only). It shows a preview of the refreshed nav, a short headline/body, a primary "Start the tour"
 * action, and a dismiss control. Both actions remove the card for good (see {@link useBrandRefreshNavTour}).
 *
 * @param props.onStartTour - Launches the tour and dismisses the card.
 * @param props.onDismiss - Dismisses the card without starting the tour.
 * @example
 * <VerticalNav
 *   customFooterItem={<BrandRefreshTourCard onStartTour={startTour} onDismiss={dismissCard} />}
 * />
 */
export const BrandRefreshTourCard: FC<BrandRefreshTourCardProps> = ({
  onStartTour,
  onDismiss
}) => <div className={containerCss}>
    <div className={cardCss}>
      <img className={previewCss} src={tourCardPreview} alt="" />
      <div className={contentCss}>
        <div className={textGroupCss}>
          <TitleText level={2} as="p" size="x-small" color={colorTextStandard}>
            {I18n.t('publisher.app_shell.brand_refresh_tour.card.title')}
          </TitleText>
          <BodyText size="small" weight="regular" color={colorTextSecondary}>
            {I18n.t('publisher.app_shell.brand_refresh_tour.card.body')}
          </BodyText>
        </div>
        <MinimalButton appearance="primary" size="medium" trailingIcon={IconArrowNarrowRight} onClick={onStartTour}>
          {I18n.t('publisher.app_shell.brand_refresh_tour.card.start')}
        </MinimalButton>
      </div>
    </div>
    <div className={dismissCss}>
      <IconButton appearance="secondary-outline" size="small" title={I18n.t('publisher.app_shell.brand_refresh_tour.card.dismiss')} onClick={onDismiss}>
        <UiIcon icon={IconX} size="small" title="" />
      </IconButton>
    </div>
  </div>;

require("./BrandRefreshTourCard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./BrandRefreshTourCard.tsx");